import * as messagingActions from "../actions/messaging"
import Immutable from 'seamless-immutable'
import moment from 'moment'
import reverseChronological from '../../lib/util/dateComparison'

const initialState = Immutable({
	unreadCount: NaN,
	[messagingActions.THREAD_STATUS_INBOX]: {
		lastReceived: 0,
		threads: []
	},
	[messagingActions.THREAD_STATUS_ARCHIVED]: {
		lastReceived: 0,
		threads: []
	},

	notification: {
		message: null,
		extra: null,
		type: null
	},

	current: {
		thread: null,
		messages: null
	},

	drafts: {},

	lastUpdatedThread: false,

	currentThreadStatusType: messagingActions.THREAD_STATUS_INBOX,

	categories: undefined
})

export default function (state = initialState, action){

	switch(action.type){
		case messagingActions.UPDATE_UNREAD_COUNT:
			return state.set('unreadCount', action.count)

		case messagingActions.THREADS_LOAD_SUCCESS:
			const loadSuccessState = state.setIn([action.threadStatus, 'lastReceived'], action.threads.length)
			let actionThreadsIncludingHidden
			if(action.pageOffset > 0){
				return loadSuccessState.setIn([action.threadStatus, 'threads'], state[action.threadStatus].threads.concat(action.threads))
			}else{
				if(action.keepHiddenThreads){
					actionThreadsIncludingHidden = state[action.threadStatus].threads
						.asMutable()
						.filter(t => t.isHidden)
						.concat(action.threads)
						.sort((a,b) => reverseChronological(a.UpdatedOn, b.UpdatedOn))
				}
				return loadSuccessState.setIn([action.threadStatus, 'threads'], (actionThreadsIncludingHidden || action.threads))
			}

		case messagingActions.THREAD_SET_CURRENT:
			return state.setIn(['current', 'thread'], action.thread)

		case messagingActions.THREAD_SET_CURRENT_MESSAGES:
			const currentThreadId = state.current.thread.Id
			const unreadMessageCount = action.messages.length - (state.current.messages || []).length
			return state
				.setIn(['current', 'thread', 'UnreadMessageCount'], unreadMessageCount)
				.setIn(['current', 'messages'], action.messages)
				.updateIn(
					[messagingActions.THREAD_STATUS_INBOX, 'threads'],
					(threads) => threads.map((thread) => thread.Id != currentThreadId ? thread : thread.set('UnreadMessageCount', unreadMessageCount))
				)

		case messagingActions.THREAD_CLOSE:
			return state.set('current', initialState.current)

		case messagingActions.THREAD_ARCHIVE:
			// Hide thread from inbox -> set "isHidden" -> true
			// Mark archived as needing refresh "dirty"

			return state
				.updateIn(
					[messagingActions.THREAD_STATUS_INBOX, 'threads'],
					(threads) => threads.map((thread) => thread.Id != action.threadId ? thread : thread.set('isHidden', true))
				)
				.update("notification", (notification) => notification.merge(
					{
						message: "Thread Archived",
						extra: action.threadId,
						type: messagingActions.THREAD_ARCHIVE
					}
				))


		case messagingActions.THREAD_ARCHIVE_UNDO:
			// show thread in inbox set "isHidden" -> false

			return state
				.updateIn(
					[messagingActions.THREAD_STATUS_INBOX, 'threads'],
					(threads) => threads.map((thread) => thread.Id != action.threadId ? thread : thread.without('isHidden'))
				)

		case messagingActions.THREAD_CLEAR_NOTIFICATION:
			return state.set('notification', initialState.notification)


		case messagingActions.THREAD_UPDATE_DRAFT_ADD_ATTACHMENT:
			return state.updateIn(['drafts', action.threadId, 'attachments'], (attachments) => {
				return attachments ? attachments.concat([action.attachment]) : [action.attachment]
			})

		case messagingActions.THREAD_UPDATE_DRAFT_UPDATE_ATTACHMENT:
			return state.updateIn(['drafts', action.threadId, 'attachments'], (attachments) => {
				if(!attachments){
					attachments = Immutable([])
				}

				const index = attachments.findIndex((attachment) => attachment.fileName == action.attachment.fileName)
				let attachmentsMutable = attachments.asMutable()
				attachmentsMutable[index == -1 ? 0 : index] = action.attachment

				return attachmentsMutable
			})

		case messagingActions.THREAD_UPDATE_DRAFT_REMOVE_ATTACHMENT:
			return state.updateIn(['drafts', action.threadId, 'attachments'], (attachments) => {
				return attachments.filter((attachment) => attachment.fileName !== action.fileName)
			})

		case messagingActions.THREAD_UPDATE_DRAFT:
			return state.setIn(['drafts', action.threadId], {
				...(state.drafts[action.threadId] || {}),
				...Immutable(action).without('threadId', 'type')
			})

		case messagingActions.THREAD_CLEAR_DRAFT:
			return state.update('drafts', (drafts) => drafts.without(String(action.threadId)))

		case messagingActions.THREAD_DRAFT_TO_MESSAGE:
			return state
				.updateIn(['current', 'messages'], (messages) => (messages || []).concat([{
					Id: 0,
					Content: state.drafts[action.threadId].content,
					Attachments: state.drafts[action.threadId].attachments,
					IsStaff: false,
					ClientName: messages && messages.length > 0 ? messages[0].ClientName : undefined,
					CreatedOn: moment().format()
				}]))
				.update('drafts', (drafts) => drafts.without(String(action.threadId)))

		case messagingActions.THREAD_READ:
			return state
				.updateIn(
					[messagingActions.THREAD_STATUS_INBOX, 'threads'],
					(threads) => threads.map((thread) => thread.Id == action.threadId ? thread.set('UnreadMessageCount', 0) : thread)
				)
				.update('unreadCount', (count) => count > 0 ? count -1 : 0)

		case messagingActions.THREAD_SET_UPDATED:
			const indexOfThread = (type) => state[type].threads.findIndex((t) => t.Id === action.threadId)
			const moveElementToStart = (arr, elementIndex) => {
				return arr.slice(elementIndex, elementIndex+1)
					.concat(arr.slice(0, elementIndex))
					.concat(arr.slice(elementIndex+1))
			}

			let newOrderedThreadList = moveElementToStart(
				state[state.currentThreadStatusType].threads,
				indexOfThread(state.currentThreadStatusType)
			)

			return state
				.set('lastUpdatedThread', action.threadId)
				.setIn([state.currentThreadStatusType, 'threads'], newOrderedThreadList)
				.updateIn(
					[state.currentThreadStatusType, 'threads'],
					(threads) => threads.map((thread) => {
						if(thread.Id == action.threadId){
							return thread.merge({UpdatedOn: action.updatedOn, LastUpdatedBy: action.updatedBy})
						}else{
							return thread
						}
					})
				)

		case messagingActions.THREAD_STATUS_TYPE_CHANGED:
			return state.set('currentThreadStatusType', action.currentStatus)

		case messagingActions.THREAD_ADD_FROM_DRAFT:
			return state
				.updateIn([messagingActions.THREAD_STATUS_INBOX, 'threads'], (threads) => [action.thread].concat(threads))
				.set('lastUpdatedThread', action.thread.Id)
				.update('drafts', (drafts) => drafts.without('default'))


		case messagingActions.THREAD_LOAD_CATEGORIES:
			return state.set('categories', action.categories)
	}

	return state
}



// WEBPACK FOOTER //
// ./node_modules/mb-app-common/store/reducers/messaging.js